import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import type { Permission } from '@cms/shared';
import { queryClient } from './lib/queryClient.js';
import { PermissionProvider } from './lib/permissions.js';
import { AuthProvider, useAuth } from './lib/auth.js';
import { ErrorBoundary } from './app/ErrorBoundary.js';
import { router } from './app/router.js';
import './styles.css';

const container = document.getElementById('root');
if (!container) throw new Error('#root missing from index.html');

/**
 * Permissions come from the session and nowhere else. An anonymous visitor holds none,
 * which is why this sits inside AuthProvider rather than beside it — and why there is no
 * dev-only "grant everything" branch, since a bypass in development is how permission gaps
 * reach production unnoticed.
 */
function SessionPermissions({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  return (
    <PermissionProvider permissions={(user?.permissions ?? []) as Permission[]}>
      {children}
    </PermissionProvider>
  );
}

createRoot(container).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SessionPermissions>
            <RouterProvider router={router} />
          </SessionPermissions>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
